import { useState, useEffect } from 'react';
import { AlertTriangle, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useFinancialData } from '../../hooks/useFinancialData';
import { useVertical } from '../../context/VerticalContext';
import { Notice } from '../ui/Notice';

/**
 * VAT threshold banner with two distinct states:
 *
 *  • Approaching (turnover ≥ 85% of the threshold but still under it) — ochre,
 *    informational, and DISMISSIBLE. Dismissing records the turnover figure at
 *    that moment; the banner then stays hidden until turnover rises again (the
 *    next paid invoice pushes the rolling-12m number past the dismissed value),
 *    at which point it reappears so the user re-checks their position.
 *
 *  • Over threshold (turnover > threshold) — RED and NOT dismissible. HMRC
 *    mandates registration within 30 days of crossing, so this stays put until
 *    the user actually registers and enables VAT in the app (which flips
 *    is_vat_registered and hides the banner entirely).
 */
export const VatWarningBanner = () => {
    const { turnover_rolling_12m, vat_threshold, is_vat_registered, loading } = useFinancialData();
    const { activeOrgId } = useVertical();

    const storageKey = activeOrgId ? `fd_vat_dismissed_${activeOrgId}` : null;
    const [dismissedAt, setDismissedAt] = useState<number | null>(null);

    // Load the per-org dismissal marker (the turnover value at dismissal time).
    useEffect(() => {
        if (!storageKey) {
            setDismissedAt(null);
            return;
        }
        const raw = localStorage.getItem(storageKey);
        setDismissedAt(raw != null && raw !== '' ? Number(raw) : null);
    }, [storageKey]);

    if (loading) return null;

    // Registered for VAT → nothing to warn about.
    if (is_vat_registered) return null;

    const overThreshold = vat_threshold > 0 && turnover_rolling_12m > vat_threshold;
    const percent = vat_threshold > 0 ? (turnover_rolling_12m / vat_threshold) * 100 : 0;
    const isApproaching = percent >= 85;

    // Below the "approaching" band and not over — nothing to show.
    if (!overThreshold && !isApproaching) return null;

    // Below the threshold: respect a dismissal until turnover climbs past the
    // value captured when the user dismissed it.
    if (!overThreshold && dismissedAt != null && turnover_rolling_12m <= dismissedAt) {
        return null;
    }

    const formatCurrency = (amount: number) =>
        new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(amount);

    const handleDismiss = () => {
        if (!storageKey) return;
        localStorage.setItem(storageKey, String(turnover_rolling_12m));
        setDismissedAt(turnover_rolling_12m);
    };

    // --- OVER THRESHOLD: red, mandatory, non-dismissible ---
    // Every fact a trader needs to act is in the always-visible text: the
    // figure, the threshold, the 30-day deadline and the GOV.UK action.
    if (overThreshold) {
        const overBy = turnover_rolling_12m - vat_threshold;
        return (
            <Notice
                tone="danger"
                icon={AlertTriangle}
                title="VAT registration required"
                actions={<GovUkLink />}
            >
                Your rolling 12-month turnover is <strong>{formatCurrency(turnover_rolling_12m)}</strong>
                {' '}— <strong>{formatCurrency(overBy)}</strong> over the <strong>{formatCurrency(vat_threshold)}</strong> threshold.
                You must register for VAT within 30 days. Once registered,{' '}
                <Link to="/settings">enable VAT in your settings</Link> to clear this alert.{' '}
                <Link to="/help/vat-threshold">What this means</Link>.
            </Notice>
        );
    }

    // --- APPROACHING: ochre, informational, dismissible ---
    const remainingToThreshold = Math.max(vat_threshold - turnover_rolling_12m, 0);
    return (
        <Notice
            tone="warning"
            icon={AlertTriangle}
            title="Approaching the VAT threshold"
            actions={<GovUkLink />}
            onDismiss={handleDismiss}
            dismissLabel="Dismiss VAT warning until turnover changes"
        >
            Rolling 12-month turnover <strong>{formatCurrency(turnover_rolling_12m)}</strong>
            {' '}— {formatCurrency(remainingToThreshold)} below the <strong>{formatCurrency(vat_threshold)}</strong> threshold.
            If you go over it, you must register within 30 days.{' '}
            <Link to="/help/vat-threshold">What this means</Link>.
        </Notice>
    );
};

const GovUkLink = () => (
    <a
        href="https://www.gov.uk/register-for-vat"
        target="_blank"
        rel="noopener noreferrer"
        className="fd-btn fd-btn-sm fd-btn-tone"
    >
        Register on GOV.UK
        <ArrowUpRight aria-hidden="true" />
        <span className="sr-only">(opens in a new tab)</span>
    </a>
);
